import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

/**
 * Address autocomplete powered by OpenStreetMap Nominatim (no API key required).
 * Calls onPick({ display, road, city, postcode, lat, lon }).
 */
export const AddressAutocomplete = ({
  value, onChange, onPick, placeholder = "Začněte psát adresu…",
  className = "", "data-testid": dataTestid = "address-autocomplete",
}) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const tRef = useRef();
  const wrapRef = useRef();

  useEffect(() => {
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = async (q) => {
    if (!q || q.length < 4) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=cz&limit=6&q=${encodeURIComponent(q)}`,
        { headers: { "Accept-Language": "cs" } }
      );
      const data = await r.json();
      setResults(data);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => search(v), 350);
  };

  const pick = (r) => {
    const a = r.address || {};
    const road = [a.road, a.house_number].filter(Boolean).join(" ");
    const city = a.city || a.town || a.village || a.municipality || a.county || "";
    const postcode = (a.postcode || "").replace(/\s/g, "");
    onChange(road || r.display_name);
    setOpen(false);
    onPick?.({
      display: r.display_name,
      road: road || r.display_name,
      city,
      postcode,
      lat: parseFloat(r.lat),
      lon: parseFloat(r.lon),
    });
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Input
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        data-testid={dataTestid}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      {loading && (
        <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
      )}
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="address-suggestions">
          {results.map((r) => (
            <button
              type="button"
              key={r.place_id}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm flex items-start gap-2 border-b border-slate-100 last:border-b-0"
              data-testid={`address-suggestion-${r.place_id}`}
            >
              <MapPin className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700 leading-snug">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
